import { useCallback, useContext } from 'react';
import type { FunctionComponent, KeyboardEvent } from 'react';

import Icon, { registerIcon } from '../Icon';
import * as caretLeftIcon from '../Icon/icons/caret-left.icon';
import * as caretRightIcon from '../Icon/icons/caret-right.icon';
import type { ForwardProps } from '../../types';
import { useI18n, useDirection } from '../../hooks';
import Flex from '../Flex';
import SummaryItem from '../SummaryItem';
import { cap } from '../../utils';

import type { MenuListHeaderProps } from './Menu.types';
import MenuContext from './Menu.context';
import { StyledMenuListHeader } from './Menu.styles';

registerIcon(caretLeftIcon, caretRightIcon);

const MenuListHeader: FunctionComponent<MenuListHeaderProps & ForwardProps> = ({
  text,
  onClick
}) => {
  const t = useI18n();
  const { arrowNavigationUnsupported } = useContext(MenuContext);
  const { start } = useDirection();

  const onKeyDown = useCallback(
    (ev: KeyboardEvent<HTMLLegendElement>) => {
      if (ev.key === 'Enter' || ev.key === `Arrow${cap(start)}`) {
        onClick();
      }
    },
    [onClick, start]
  );

  return (
    <Flex
      container={{ alignItems: 'center' }}
      as={StyledMenuListHeader}
      onClick={onClick}
      onKeyDown={onKeyDown}
      data-collapse='true'
      aria-expanded
      aria-label={`${t('menu_expanded', [text || ''])} ${
        arrowNavigationUnsupported
          ? t('menu_item_collapse_shift_space')
          : t('menu_item_collapse_arrow')
      }`}
    >
      <SummaryItem visual={<Icon name={`caret-${start}`} />} primary={text} />
    </Flex>
  );
};

export default MenuListHeader;
